import type { GetAccountUsageQuery } from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useAccountPermissions } from '@frameio/permissions';
import { AlertBanner } from '@frameio/vapor';
import { useIsOnUsagePage } from '../hooks/useIsOnUsagePage';
import { BannerWrapper } from './BannerWrapper';
import TEXT from './generated/TEXT/WorkspaceAlertBanner';
import { SupportButton } from './SupportButton';
import { UniversalLink } from './UniversalLink';

TEXT.DEFINE(
  {
    /** Title for billing managers */
    titleWithPermission: (workspaceCount: number, workspaceLimit: number) =>
      `Workspace Limit Exceeded (${workspaceCount} of ${workspaceLimit} Workspaces)`,

    /** Title for non-billing users */
    titleNoPermission: 'Workspace Limit Exceeded',

    /** Message for Adobe-managed accounts without billing permissions */
    detailsNoPermissionAdobe: 'Contact your admin for assistance.',

    /** Message for non-Adobe accounts without billing permissions */
    detailsNoPermission: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance.
      </>
    ),

    /** Message for billing managers */
    detailsWithPermission: () => (
      <>
        <SupportButton>Contact support</SupportButton> for assistance.
      </>
    ),
  },
  { UniversalLink, SupportButton }
);

const getDetails = (
  canManageBilling: boolean,
  isManagedByAdobe: boolean,
  ownerEmail: string
) => {
  if (canManageBilling) {
    return TEXT.detailsWithPermission();
  }
  if (isManagedByAdobe) {
    return TEXT.detailsNoPermissionAdobe;
  }
  return TEXT.detailsNoPermission(ownerEmail);
};

type WorkspaceAlertBannerProps = {
  account: NonNullable<GetAccountUsageQuery['account']>;
};

export function WorkspaceAlertBanner({ account }: WorkspaceAlertBannerProps) {
  const accountId = account.id;
  const canManageBilling =
    useAccountPermissions({
      accountId,
      check: 'canManageBilling',
    }) ?? false;

  const workspaceCount =
    account.subscription?.limitedResources?.workspaces?.used53 ?? 0;

  const workspaceLimit =
    account.subscription?.limitedResources?.workspaces?.limit53;
  const hasUnlimitedWorkspaceLimit = workspaceLimit === null;
  const isAccountLocked = !!account.lockedAt;
  const isOnUsagePage = useIsOnUsagePage();

  const showBanner =
    !hasUnlimitedWorkspaceLimit &&
    workspaceCount > (workspaceLimit ?? 0) &&
    !isAccountLocked &&
    !isOnUsagePage;

  const ownerEmail = account.owner?.email ?? '';
  const isManagedByAdobe = isAccountAdobeManaged(account.managedBy);

  if (!showBanner) return null;

  const title = canManageBilling
    ? TEXT.titleWithPermission(workspaceCount, workspaceLimit ?? 0)
    : TEXT.titleNoPermission;

  const details = getDetails(canManageBilling, isManagedByAdobe, ownerEmail);

  return (
    <BannerWrapper>
      <AlertBanner details={details} title={title} variant="notice" />
    </BannerWrapper>
  );
}
